import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, Key, Search, House, Building2, Plus, Check, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PropertyCard from "@/components/PropertyCard";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    fetchFeaturedProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties/featured`);
      setProperties(response.data.slice(0, 4));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/properties?city=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate("/properties");
    }
  };

  const whyFeatures = t('whyChoose.features');

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-32" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1615634364452-8daf3851f2f1?w=1600"
            alt="Dream home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Heart className="w-6 h-6 text-primary fill-primary animate-pulse-heart" />
              <Key className="w-5 h-5 text-accent" />
              <Badge className="bg-white/20 text-white">🇨🇦 {t('hero.badge')}</Badge>
            </div>

            <h1 className="font-heading text-5xl md:text-7xl font-medium text-white leading-tight mb-6" data-testid="hero-title">
              {t('hero.title')} <span className="gradient-text">{t('hero.titleHighlight')}</span>
            </h1>

            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              {t('hero.subtitle')}
            </p>

            {/* Search Box */}
            <div className="bg-white/95 backdrop-blur rounded-2xl p-4 shadow-hover" data-testid="hero-search">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder={t('hero.searchPlaceholder')}
                    className="pl-12 h-14 rounded-xl border-border"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="h-14 px-8 rounded-xl text-lg" data-testid="hero-search-btn">
                  {t('hero.searchBtn')}
                </Button>
              </form>
            </div>

            {/* Quick Links */}
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/properties?listing_type=buy">
                <Button variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-charcoal">
                  <House className="w-4 h-4 mr-2" /> {t('hero.buyBtn')}
                </Button>
              </Link>
              <Link to="/properties?listing_type=rent">
                <Button variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-charcoal">
                  <Building2 className="w-4 h-4 mr-2" /> {t('hero.rentBtn')}
                </Button>
              </Link>
              <Link to="/list-property">
                <Button className="rounded-full bg-accent hover:bg-accent/90">
                  <Plus className="w-4 h-4 mr-2" /> {t('hero.listBtn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 bg-secondary/30" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-primary mb-2">
              {t('featured.subtitle')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-medium text-charcoal">
              {t('featured.title')}
            </h2>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={`featured-skeleton-${i}`} className="bg-white rounded-2xl h-96 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button variant="outline" className="rounded-full px-8">
                {t('featured.viewAll')} <span className="ml-2">→</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Agent CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 to-accent/10" data-testid="agent-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-hover">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <Badge className="bg-accent text-white mb-4">{t('agentCta.badge')}</Badge>
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-charcoal mb-4">
                  {t('agentCta.title')}
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  {t('agentCta.subtitle')}
                </p>

                <div className="space-y-3 mb-8">
                  {t('agentCta.features').map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-charcoal">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/list-property">
                  <Button className="rounded-full px-8">
                    <Plus className="w-5 h-5 mr-2" /> {t('agentCta.cta')}
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-secondary/50 rounded-2xl p-6 text-center">
                  <House className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="font-heading text-2xl font-semibold text-charcoal">500+</p>
                  <p className="text-sm text-muted-foreground">{t('agentCta.stats.properties')}</p>
                </div>
                <div className="bg-secondary/50 rounded-2xl p-6 text-center">
                  <Users className="w-8 h-8 text-accent mx-auto mb-2" />
                  <p className="font-heading text-2xl font-semibold text-charcoal">100+</p>
                  <p className="text-sm text-muted-foreground">{t('agentCta.stats.agents')}</p>
                </div>
                <div className="bg-secondary/50 rounded-2xl p-6 text-center">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="font-heading text-2xl font-semibold text-charcoal">10K+</p>
                  <p className="text-sm text-muted-foreground">{t('agentCta.stats.visitors')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Owner CTA Section */}
      <section className="py-20 bg-white" data-testid="owner-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-8 md:p-12 shadow-hover text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <Badge className="bg-white/20 text-white mb-4">{t('ownerCta.badge')}</Badge>
                <h2 className="font-heading text-3xl md:text-4xl font-medium mb-4">
                  {t('ownerCta.title')}
                </h2>
                <p className="text-lg text-white/80 mb-6">
                  {t('ownerCta.subtitle')}
                </p>

                <div className="space-y-3 mb-8">
                  {t('ownerCta.features').map((item, i) => (
                    <div key={`owner-feature-${i}`} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/owner">
                  <Button className="rounded-full px-8 bg-white text-primary hover:bg-white/90">
                    <Plus className="w-5 h-5 mr-2" /> {t('ownerCta.cta')}
                  </Button>
                </Link>
              </div>

              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600"
                  alt="Property owner"
                  className="rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-primary mb-2">
                {t('whyChoose.subtitle')}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-medium text-charcoal mb-6">
                {t('whyChoose.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t('whyChoose.description')}
              </p>

              <div className="space-y-6">
                {whyFeatures.map((feature, index) => (
                  <div 
                    key={`why-feature-${index}`}
                    className="flex gap-4 p-4 rounded-2xl bg-secondary/50 border border-border/50"
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white shadow-soft flex items-center justify-center">
                      {index === 0 && <Heart className="w-8 h-8 text-primary" />}
                      {index === 1 && <Key className="w-8 h-8 text-accent" />}
                      {index === 2 && <Search className="w-8 h-8 text-primary" />}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-medium text-charcoal mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7642004/pexels-photo-7642004.jpeg?w=800"
                alt="Hand holding house key"
                className="rounded-3xl shadow-hover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-hover">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-primary fill-primary" />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-semibold text-charcoal">500+</p>
                    <p className="text-sm text-muted-foreground">{t('whyChoose.happyFamilies')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
